import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, SearchIcon } from "@chakra-ui/icons";
import {
  FiArchive,
  FiCornerUpLeft,
  FiEdit3,
  FiFilePlus,
  FiFileText,
  FiSend,
  FiUser,
  FiDollarSign,
  FiCheckCircle,
} from "react-icons/fi";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import Attachment from "../../../components/forms";
import Forward from "../../../components/forms/Forward";
import { GlobalContext } from "../../../context/Provider";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import attachmentKeys from "../../../utils/attachmentKeys";
import { getPatientsApi } from "../../../api/patients";
import {
  createFormApi,
  deleteRequestApi,
  forwardRequestApi,
  getFormsApi,
  getPatientFormsApi,
  markRequestFormPaidApi,
  returnRequestApi,
  submitAttachmentApi,
} from "../../../api/request";
import { PERMISSIONS } from "../../../auth/permissions";
import FileList from "../../files/FileList";
import PrescriptionItemsDisplay from "../../forms/PrescriptionItemsDisplay";

const workerName = (worker) =>
  worker
    ? `${worker.firstName || ""} ${worker.lastName || ""}`.trim()
    : "Unknown";

const patientName = (patient) =>
  patient
    ? `${patient.bioData?.surname || ""} ${patient.bioData?.otherNames || ""}`.trim()
    : "Unknown patient";

const currency = (value) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const PaymentSummary = ({ payment = {} }) => (
  <Box
    mx="5"
    mb="5"
    borderWidth="1px"
    borderColor={payment.paid ? "green.200" : "gray.200"}
    borderRadius="xl"
    overflow="hidden"
  >
    <Flex
      px="4"
      py="3"
      bg={payment.paid ? "green.50" : "gray.50"}
      align="center"
      justify="space-between"
      gap="3"
    >
      <HStack>
        <Icon as={FiDollarSign} />
        <Text fontWeight="800" fontSize="sm">
          Payment details
        </Text>
      </HStack>
      <Badge colorScheme={payment.paid ? "green" : "orange"}>
        {payment.paid ? "Paid" : "Not paid"}
      </Badge>
    </Flex>
    <SimpleGrid columns={{ base: 1, sm: 3 }} gap="3" p="4">
      <Box>
        <Text fontSize="xs" color="gray.500">
          Full amount
        </Text>
        <Text fontWeight="800">{currency(payment.fullAmount)}</Text>
      </Box>
      <Box>
        <Text fontSize="xs" color="gray.500">
          Final Discount
        </Text>
        <Text fontWeight="800">{Number(payment.discount) || 0}%</Text>
      </Box>
      <Box>
        <Text fontSize="xs" color="gray.500">
          Total payable
        </Text>
        <Text fontWeight="800" color="brand.700">
          {currency(payment.totalAmount)}
        </Text>
      </Box>
    </SimpleGrid>
    {payment.markedPaidBy && (
      <Text px="4" pb="4" fontSize="xs" color="gray.500">
        Marked paid by {workerName(payment.markedPaidBy)}
        {payment.markedPaidAt
          ? ` on ${new Date(payment.markedPaidAt).toLocaleString()}`
          : ""}
      </Text>
    )}
  </Box>
);

const FormContent = ({ form }) => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap="3">
    {Object.entries(form || {}).map(([key, value]) =>
      ["prescriptionItems", "injectionItems"].includes(key) ? (
        <Box key={key} gridColumn={{ md: "1 / -1" }}>
          <Text
            mb="2"
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
          >
            {key === "injectionItems" ? "Injections" : "Medications"}
          </Text>
          <PrescriptionItemsDisplay items={value} />
        </Box>
      ) : (
        <Box key={key} bg="gray.50" borderRadius="lg" px="3" py="2">
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
          >
            {attachmentKeys[key] || key}
          </Text>
          <Text mt="1" fontSize="sm" color="gray.800" wordBreak="break-word">
            {Array.isArray(value)
              ? value.map((item) => attachmentKeys[item] || item).join(", ")
              : value?.toString() || "—"}
          </Text>
        </Box>
      ),
    )}
  </SimpleGrid>
);

const RequestFormCard = ({
  requestForm,
  currentWorkerId,
  patientContext,
  onViewPatient,
  onEdit,
  onSend,
  onReturn,
  onArchive,
  onMarkPaid,
  cashPointMode,
  isMarkingPaid,
  isReturning,
}) => {
  const canManage =
    !requestForm.deleted && requestForm.currentHolder?._id === currentWorkerId;

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="2xl"
      overflow="hidden"
    >
      <Box p="5">
        <Flex justify="space-between" align="flex-start" gap="4">
          <HStack align="flex-start" spacing="3" minW="0">
            <Center
              w="42px"
              h="42px"
              flexShrink="0"
              borderRadius="xl"
              bg="brand.50"
              color="brand.600"
            >
              <Icon as={FiFileText} boxSize="5" />
            </Center>
            <Box minW="0">
              <Heading size="sm" textTransform="capitalize" noOfLines={1}>
                {requestForm.type} form
              </Heading>
              <Text mt="1" fontSize="sm" color="gray.500">
                Version {requestForm.version || 1}
              </Text>
            </Box>
          </HStack>
          <Badge
            colorScheme={
              requestForm.deleted ? "red" : canManage ? "green" : "purple"
            }
            borderRadius="full"
            px="3"
            py="1"
            whiteSpace="nowrap"
          >
            {requestForm.deleted
              ? "Archived"
              : canManage
                ? "Needs your action"
                : "Read only"}
          </Badge>
        </Flex>

        <Stack mt="5" spacing="3" fontSize="sm">
          <Flex justify="space-between" gap="4">
            <Text color="gray.500">Patient</Text>
            <Text fontWeight="700" textAlign="right">
              {patientName(requestForm.patient)}
            </Text>
          </Flex>
          <Flex justify="space-between" gap="4">
            <Text color="gray.500">Current holder</Text>
            <Text fontWeight="700" textAlign="right">
              {canManage ? "You" : workerName(requestForm.currentHolder)}
            </Text>
          </Flex>
          <Flex justify="space-between" gap="4">
            <Text color="gray.500">Created by</Text>
            <Text fontWeight="700" textAlign="right">
              {workerName(requestForm.createdBy)}
            </Text>
          </Flex>
          {requestForm.currentHolder?.roomNumber && (
            <Flex justify="space-between" gap="4">
              <Text color="gray.500">Current room</Text>
              <Text fontWeight="700" color="brand.600" textAlign="right">
                Room{" "}
                {String(requestForm.currentHolder.roomNumber).replace(
                  /^room\s*/i,
                  "",
                )}
              </Text>
            </Flex>
          )}
          {requestForm.precedingHolder && (
            <Flex justify="space-between" gap="4">
              <Text color="gray.500">Received from</Text>
              <Text fontWeight="700" textAlign="right">
                {workerName(requestForm.precedingHolder)}
              </Text>
            </Flex>
          )}
        </Stack>

        {!patientContext && (
          <Button
            mt="4"
            size="sm"
            variant="link"
            leftIcon={<FiUser />}
            onClick={() => onViewPatient(requestForm.patient?._id)}
          >
            Open patient record
          </Button>
        )}
      </Box>

      <Accordion allowToggle borderTopWidth="1px" borderColor="gray.100">
        <AccordionItem border="0">
          <AccordionButton px="5" py="3" _hover={{ bg: "gray.50" }}>
            <Text flex="1" textAlign="left" fontWeight="700" fontSize="sm">
              View form content
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px="5" pb="5">
            <FormContent form={requestForm.form} />
            <FileList files={requestForm.files || []} />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <PaymentSummary payment={requestForm.payment} />

      {canManage && (
        <Flex
          p="4"
          bg="gray.50"
          borderTopWidth="1px"
          borderColor="gray.100"
          gap="2"
          flexWrap="wrap"
        >
          {!cashPointMode && (
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiEdit3 />}
              onClick={() => onEdit(requestForm)}
            >
              Edit
            </Button>
          )}
          {cashPointMode && !requestForm.payment?.paid && (
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<FiCheckCircle />}
              isLoading={isMarkingPaid}
              onClick={() => onMarkPaid(requestForm)}
            >
              Mark as paid
            </Button>
          )}
          <Button
            size="sm"
            leftIcon={<FiSend />}
            onClick={() => onSend(requestForm)}
          >
            Send
          </Button>
          {requestForm.precedingHolder && (
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiCornerUpLeft />}
              isLoading={isReturning}
              onClick={() => onReturn(requestForm)}
            >
              Return to {requestForm.precedingHolder.firstName || "sender"}
            </Button>
          )}
          {!cashPointMode && (
            <Button
              ml={{ md: "auto" }}
              size="sm"
              colorScheme="red"
              variant="ghost"
              leftIcon={<FiArchive />}
              onClick={() => onArchive(requestForm)}
            >
              Archive
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
};

const RequestFormDrawer = ({ requestFormDrawer, patient }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { authState, can } = useContext(GlobalContext);
  const forwardModal = useDisclosure();
  const attachmentModal = useDisclosure();
  const attachmentCreateModal = useDisclosure();
  const archiveDialog = useDisclosure();
  const paymentDialog = useDisclosure();
  const archiveCancelRef = useRef();
  const paymentCancelRef = useRef();
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(patient || null);
  const [view, setView] = useState("forms");
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedValue(searchText);

  useEffect(() => setSelectedPatient(patient || null), [patient]);

  const currentFormsQuery = useQuery(
    ["currentRequestForms", authState.user?._id],
    getFormsApi,
    { enabled: requestFormDrawer.isOpen && !patient },
  );
  const patientFormsQuery = useQuery(
    ["patientRequestForms", patient?._id],
    () => getPatientFormsApi(patient?._id),
    { enabled: requestFormDrawer.isOpen && Boolean(patient?._id) },
  );
  const patientsQuery = useQuery(
    ["requestFormPatientSearch", debouncedSearch],
    () => getPatientsApi(debouncedSearch),
    {
      enabled: requestFormDrawer.isOpen && view === "patients",
      keepPreviousData: true,
    },
  );

  const formsQuery = patient ? patientFormsQuery : currentFormsQuery;
  const forms = formsQuery.data?.data?.requestForms || [];
  const patients = patientsQuery.data?.data || [];
  const cashPointMode =
    String(authState.user?.unit || "")
      .trim()
      .toLowerCase() === "cash point" &&
    can(PERMISSIONS.REQUEST_FORMS_MARK_PAID);

  const refreshForms = async () => {
    if (currentFormsQuery.isFetched) await currentFormsQuery.refetch();
    if (patientFormsQuery.isFetched) await patientFormsQuery.refetch();
  };

  const updateMutation = useMutation(submitAttachmentApi, {
    onSuccess: async () => {
      attachmentModal.onClose();
      setSelectedForm(null);
      await refreshForms();
      toast({
        title: "Form updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to update form",
        description:
          error.response?.data?.msg || "Please refresh and try again",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const forwardMutation = useMutation(forwardRequestApi, {
    onSuccess: async () => {
      forwardModal.onClose();
      setSelectedForm(null);
      await refreshForms();
      toast({
        title: "Form sent",
        description: "The receiving worker can now review and update it.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to send form",
        description:
          error.response?.data?.msg || "Please refresh and try again",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const returnMutation = useMutation(returnRequestApi, {
    onSuccess: async () => {
      await refreshForms();
      toast({
        title: "Form returned",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to return form",
        description:
          error.response?.data?.msg || "Please refresh and try again",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const archiveMutation = useMutation(deleteRequestApi, {
    onSuccess: async () => {
      archiveDialog.onClose();
      setSelectedForm(null);
      await refreshForms();
      toast({
        title: "Form archived",
        description: "It remains available in the patient audit history.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to archive form",
        description:
          error.response?.data?.msg || "Please refresh and try again",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const markPaidMutation = useMutation(markRequestFormPaidApi, {
    onSuccess: async () => {
      paymentDialog.onClose();
      setSelectedForm(null);
      await refreshForms();
      toast({
        title: "Payment confirmed",
        description: "The payment record is now locked.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to confirm payment",
        description:
          error.response?.data?.msg || "Please refresh and try again",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const createMutation = useMutation(createFormApi, {
    onSuccess: async () => {
      attachmentCreateModal.onClose();
      setView("forms");
      await refreshForms();
      toast({
        title: "Request form created",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to create form",
        description: error.response?.data?.msg || "Please try again",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const startCreation = () => {
    if (cashPointMode) return;
    if (patient || selectedPatient) attachmentCreateModal.onOpen();
    else setView("patients");
  };

  const resetDrawerState = () => {
    setView("forms");
    setSearchText("");
    setSelectedForm(null);
    setSelectedPatient(patient || null);
  };

  return (
    <>
      <Drawer
        placement="right"
        size="lg"
        isOpen={requestFormDrawer.isOpen}
        onClose={requestFormDrawer.onClose}
        onCloseComplete={resetDrawerState}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton top="5" />
          <DrawerHeader
            px={{ base: "5", md: "7" }}
            py="5"
            borderBottomWidth="1px"
          >
            <HStack spacing="3">
              {view === "patients" && (
                <Button
                  aria-label="Back to forms"
                  variant="ghost"
                  size="sm"
                  px="2"
                  onClick={() => setView("forms")}
                >
                  <ArrowBackIcon boxSize="5" />
                </Button>
              )}
              <Box>
                <Heading size="md">
                  {view === "patients" ? "Choose a patient" : "Request forms"}
                </Heading>
                <Text mt="1" fontSize="sm" color="gray.500">
                  {view === "patients"
                    ? "Select the patient this clinical form belongs to."
                    : patient
                      ? `Forms associated with ${patientName(patient)}`
                      : "Forms currently assigned to you."}
                </Text>
              </Box>
            </HStack>
          </DrawerHeader>

          <DrawerBody px={{ base: "4", md: "7" }} py="6" bg="gray.50">
            {view === "patients" ? (
              <Box>
                <InputGroup size="lg">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    bg="white"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Search name, patient ID, unit or NHIS no."
                  />
                </InputGroup>
                {patientsQuery.isLoading ? (
                  <Center minH="280px">
                    <Spinner color="brand.500" />
                  </Center>
                ) : patientsQuery.isError ? (
                  <Center
                    minH="280px"
                    flexDirection="column"
                    textAlign="center"
                  >
                    <Text fontWeight="700">Unable to load patients</Text>
                    <Button
                      mt="3"
                      size="sm"
                      variant="outline"
                      onClick={() => patientsQuery.refetch()}
                    >
                      Try again
                    </Button>
                  </Center>
                ) : !patients.length ? (
                  <Center
                    minH="280px"
                    flexDirection="column"
                    textAlign="center"
                  >
                    <Icon as={FiUser} boxSize="7" color="gray.400" />
                    <Text mt="3" fontWeight="700">
                      No patients found
                    </Text>
                    <Text mt="1" color="gray.500" fontSize="sm">
                      Try another name or patient identifier.
                    </Text>
                  </Center>
                ) : (
                  <VStack mt="5" spacing="3" align="stretch">
                    {patients.map((item) => (
                      <Flex
                        key={item._id}
                        bg="white"
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="xl"
                        p="4"
                        justify="space-between"
                        align="center"
                        gap="4"
                        _hover={{ borderColor: "brand.300", boxShadow: "sm" }}
                      >
                        <Box minW="0">
                          <Text
                            fontWeight="800"
                            textTransform="capitalize"
                            noOfLines={1}
                          >
                            {patientName(item)}
                          </Text>
                          <Text mt="1" color="gray.500" fontSize="sm">
                            {item.patientNumber ||
                              item.bioData?.unitNo ||
                              "No patient number"}
                          </Text>
                        </Box>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedPatient(item);
                            attachmentCreateModal.onOpen();
                          }}
                        >
                          Select
                        </Button>
                      </Flex>
                    ))}
                  </VStack>
                )}
              </Box>
            ) : formsQuery.isLoading ? (
              <Center minH="350px">
                <Spinner color="brand.500" size="lg" />
              </Center>
            ) : formsQuery.isError ? (
              <Center minH="350px" flexDirection="column" textAlign="center">
                <Text fontWeight="700">Unable to load request forms</Text>
                <Text mt="1" color="gray.500" fontSize="sm">
                  Check your connection and try again.
                </Text>
                <Button
                  mt="4"
                  size="sm"
                  variant="outline"
                  onClick={() => formsQuery.refetch()}
                >
                  Try again
                </Button>
              </Center>
            ) : !forms.length ? (
              <Center
                minH="350px"
                flexDirection="column"
                textAlign="center"
                px="8"
              >
                <Center
                  w="60px"
                  h="60px"
                  bg="brand.50"
                  color="brand.600"
                  borderRadius="full"
                >
                  <Icon as={FiFileText} boxSize="7" />
                </Center>
                <Heading mt="4" size="sm">
                  No request forms here
                </Heading>
                <Text mt="2" color="gray.500" fontSize="sm">
                  {patient
                    ? "Create a form for this patient when a clinical request is needed."
                    : "Forms sent to you by another staff member will appear here."}
                </Text>
                {!cashPointMode && (
                  <Button
                    mt="5"
                    size="sm"
                    leftIcon={<FiFilePlus />}
                    onClick={startCreation}
                  >
                    Create request form
                  </Button>
                )}
              </Center>
            ) : (
              <VStack spacing="4" align="stretch">
                {forms.map((requestForm) => (
                  <RequestFormCard
                    key={requestForm._id}
                    requestForm={requestForm}
                    currentWorkerId={authState.user?._id}
                    patientContext={Boolean(patient)}
                    onViewPatient={(id) => navigate(`/patient/${id}`)}
                    onEdit={(form) => {
                      setSelectedForm(form);
                      setSelectedPatient(form.patient);
                      attachmentModal.onOpen();
                    }}
                    onSend={(form) => {
                      setSelectedForm(form);
                      forwardModal.onOpen();
                    }}
                    onReturn={(form) => returnMutation.mutate(form._id)}
                    onArchive={(form) => {
                      setSelectedForm(form);
                      archiveDialog.onOpen();
                    }}
                    onMarkPaid={(form) => {
                      setSelectedForm(form);
                      paymentDialog.onOpen();
                    }}
                    cashPointMode={cashPointMode}
                    isMarkingPaid={markPaidMutation.isLoading}
                    isReturning={returnMutation.isLoading}
                  />
                ))}
              </VStack>
            )}
          </DrawerBody>

          {view === "forms" && forms.length > 0 && !cashPointMode && (
            <DrawerFooter borderTopWidth="1px" bg="white">
              <Button
                w="full"
                leftIcon={<FiFilePlus />}
                onClick={startCreation}
              >
                Create request form
              </Button>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      <Forward
        heading="Send form to"
        onForward={(workerId) =>
          forwardMutation.mutate({
            nextWorker: workerId,
            requestFormId: selectedForm?._id,
          })
        }
        forwardModal={forwardModal}
      />

      <Attachment
        bioData={selectedPatient?.bioData}
        mode="edit"
        isSubmitting={updateMutation.isLoading}
        onSubmitAttachment={(data) =>
          updateMutation.mutate({
            ...data,
            requestFormId: selectedForm?._id,
            version: selectedForm?.version,
          })
        }
        setAttachmentToEdit={setSelectedForm}
        attachmentToEdit={selectedForm}
        attachmentModal={attachmentModal}
      />

      <Attachment
        bioData={selectedPatient?.bioData}
        isSubmitting={createMutation.isLoading}
        onSubmitAttachment={(data) =>
          createMutation.mutate({ ...data, patientId: selectedPatient?._id })
        }
        attachmentModal={attachmentCreateModal}
      />

      <AlertDialog
        isOpen={archiveDialog.isOpen}
        leastDestructiveRef={archiveCancelRef}
        onClose={archiveDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader>Archive request form?</AlertDialogHeader>
            <AlertDialogBody>
              This removes the form from the active workflow, but keeps it in
              the patient’s audit history.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={archiveCancelRef}
                variant="ghost"
                onClick={archiveDialog.onClose}
              >
                Cancel
              </Button>
              <Button
                ml="3"
                colorScheme="red"
                isLoading={archiveMutation.isLoading}
                onClick={() => archiveMutation.mutate(selectedForm?._id)}
              >
                Archive form
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={paymentDialog.isOpen}
        leastDestructiveRef={paymentCancelRef}
        onClose={paymentDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader>Confirm this payment?</AlertDialogHeader>
            <AlertDialogBody>
              Confirm that {currency(selectedForm?.payment?.totalAmount)} has
              been received. This action permanently locks the payment fields.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={paymentCancelRef}
                variant="ghost"
                onClick={paymentDialog.onClose}
              >
                Cancel
              </Button>
              <Button
                ml="3"
                colorScheme="green"
                isLoading={markPaidMutation.isLoading}
                onClick={() =>
                  markPaidMutation.mutate({
                    requestFormId: selectedForm?._id,
                    version: selectedForm?.version,
                  })
                }
              >
                Confirm payment
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default RequestFormDrawer;
